'use client';

import type { Filter, MeasurementUnit } from '@cvent/venue-search-shared/types';
import { type Dictionary } from '@/dictionaries';
import { LOCALE_HEADER, type Locale } from '@/i18n';
import type { CsnSearchVenue } from '@/models/CsnSearchVenue';
import { useCallback } from 'react';
import { useSearchParams } from 'next/navigation';
import { queryStringConstants } from '@/constants/queryConstants';
import { uniqBy } from 'lodash';
import type { LatLng, LatLngBoundsLiteral } from '@/utils/maps/mapUtils';
import useFullMap from '@/hooks/useFullMap';
import type { VenueHighlightStrings } from '@/utils/venueHighlightsHelper';
import type { MapSize } from '@/utils/maps/mapSizeOptions';
import MapViewLoader from '@/components/maps/MapViewLoader';
import { getMappedShoppingCartItems } from '@/utils/cart/getShoppingCartItem';
import useSpinner from '@/hooks/useSpinner';
import type { QueryParserResult } from '@/fetchers/getQueryParserResult';
import { limitFourDiamondAndDiamondPlusAds } from '@/utils/venueListHelper';
import type { Criteria } from '@/fetchers/getFastResponderCriteria';

type Props = {
  dictionary: Partial<Dictionary>;
  rates: boolean;
  venueHighlightStrings: VenueHighlightStrings;
  locale: Locale;
  isLoggedIn: boolean;
  filters: Filter;
  searchTerm: string;
  measurementUnit: MeasurementUnit;
  canShowDistanceOnCards: boolean;
  envVariables: Record<string, string>;
  mapSizeOptions: Array<MapSize>;
  experiments: {
    venueSearchV2Enabled: number;
    isFastResponderEnabled: number;
  };
  isNotificationBannerVisible: boolean;
  defaultMaxDistance: number;
  fastResponderCriteria: Criteria;
  adSessionId: string;
};

export default function MapViewContainer({
  dictionary,
  rates,
  venueHighlightStrings,
  envVariables,
  mapSizeOptions,
  locale,
  isLoggedIn,
  filters,
  searchTerm,
  measurementUnit,
  canShowDistanceOnCards,
  experiments,
  isNotificationBannerVisible,
  defaultMaxDistance,
  fastResponderCriteria,
  adSessionId
}: Props): JSX.Element {
  const params = useSearchParams();
  const { setShowMapLoaderInMediumView } = useSpinner();

  const {
    mapVenuesForFullMap,
    mapVenuesWithAds,
    mapSearchParams,
    mapGeoLocationData,
    mapPreviousGeoData,
    mapSearchCoordinates,
    mapShoppingCartItems,
    setMapVenuesForFullMap,
    setMapVenuesForListView,
    setMapSearchParams,
    setMapSearchCoordinates,
    setMapGeoLocationData,
    setMapPreviousGeoData,
    setAccountTagOptions,
    setApplicableFiltersInState,
    setMapShoppingCartItems,
    setIsListViewLoading
  } = useFullMap();

  // This function is called on search as I move of FULL_MAP
  const triggerVenueFetch = useCallback(
    async (
      searchTermCenter: { lat: number; lng: number },
      newPrevGeo: string, // current geo
      previousGeoData: QueryParserResult | null, // previous geo decoded data
      newCenter?: LatLng,
      newBoundingBox?: LatLngBoundsLiteral
    ): Promise<void> => {
      setIsListViewLoading(true);
      // check if this is a case of re-center
      const recenter = !(newCenter && newBoundingBox);

      // Fetch venues
      fetch('/venues/api/venues', {
        method: 'POST',
        headers: {
          [LOCALE_HEADER]: locale
        },
        body: JSON.stringify({
          venueSearchV2Enabled: experiments.venueSearchV2Enabled,
          searchTerm,
          sortType: params.get(queryStringConstants.SORT),
          filter: filters,
          measurementUnit,
          locale,
          isLoggedIn,
          isFastResponderEnabled: experiments.isFastResponderEnabled,
          defaultMaxDistance,
          searchTermCenter,
          newPrevGeo,
          newCenter,
          newBoundingBox,
          mapGeoLocationData
        })
      }).then(response => {
        if (response.ok) {
          response.json().then(data => {
            // Update states
            const venuesWithAdsForListView = limitFourDiamondAndDiamondPlusAds(data.venues);
            const uniqueVenues = uniqBy(venuesWithAdsForListView, (venue: CsnSearchVenue) => venue.id);
            setMapVenuesForFullMap(uniqueVenues); // setting unique venues for full map pins
            setMapVenuesForListView(venuesWithAdsForListView); // setting venues with ads for list view
            setMapGeoLocationData(data.newGeoLocationData);
            setMapPreviousGeoData(previousGeoData);
            setMapSearchCoordinates(
              recenter
                ? { lat: searchTermCenter.lat, lon: searchTermCenter.lng }
                : { lat: newCenter.lat, lon: newCenter.lng }
            );
            setMapSearchParams({
              ...mapSearchParams,
              geo: data.newGeoCode,
              lat: searchTermCenter.lat.toString(),
              lng: searchTermCenter.lng.toString()
            });
            setAccountTagOptions(data.accountTags);
            setApplicableFiltersInState(data.applicableFilters);
            setMapShoppingCartItems(
              getMappedShoppingCartItems(
                uniqueVenues,
                locale,
                searchTerm,
                envVariables.CVENT_GUEST_DOMAIN,
                envVariables.BASE_PATH
              )
            );
            // Update the URL without refreshing
            const currentUrl = new URL(window.location.href);
            const newSearchParams = new URLSearchParams(currentUrl.search);
            newSearchParams.set(queryStringConstants.GEO, data.newGeoCode as string);
            if (!recenter) {
              // update params
              newSearchParams.set(queryStringConstants.PREV_GEO, newPrevGeo as string);
              newSearchParams.set(queryStringConstants.LAT, searchTermCenter.lat.toString());
              newSearchParams.set(queryStringConstants.LNG, searchTermCenter.lng.toString());
            } else {
              // remove params
              newSearchParams.delete(queryStringConstants.PREV_GEO);
              newSearchParams.delete(queryStringConstants.LAT);
              newSearchParams.delete(queryStringConstants.LNG);
            }
            currentUrl.search = newSearchParams.toString();
            window.history.pushState({}, '', currentUrl);
            setIsListViewLoading(false);
          });
        } else {
          setShowMapLoaderInMediumView(false);
        }
      });
    },
    [
      setIsListViewLoading,
      locale,
      experiments.venueSearchV2Enabled,
      experiments.isFastResponderEnabled,
      searchTerm,
      params,
      filters,
      measurementUnit,
      isLoggedIn,
      defaultMaxDistance,
      mapGeoLocationData,
      setMapVenuesForFullMap,
      setMapVenuesForListView,
      setMapGeoLocationData,
      setMapPreviousGeoData,
      setMapSearchCoordinates,
      setMapSearchParams,
      mapSearchParams,
      setAccountTagOptions,
      setApplicableFiltersInState,
      setMapShoppingCartItems,
      envVariables.CVENT_GUEST_DOMAIN,
      envVariables.BASE_PATH,
      setShowMapLoaderInMediumView
    ]
  );
  return (
    <MapViewLoader
      adSessionId={adSessionId}
      center={{ ...mapSearchCoordinates, lng: mapSearchCoordinates.lon }}
      venuesForFullMap={mapVenuesForFullMap}
      venuesWithAds={mapVenuesWithAds}
      items={mapShoppingCartItems}
      locale={locale}
      filters={filters}
      dictionary={dictionary}
      venueHighlightStrings={venueHighlightStrings}
      triggerVenueFetch={triggerVenueFetch}
      rates={rates}
      envVariables={envVariables}
      searchTerm={searchTerm}
      searchParams={mapSearchParams}
      measurementUnit={measurementUnit}
      canShowDistanceOnCards={canShowDistanceOnCards}
      mapSizeOptions={mapSizeOptions}
      previousGeoLocationData={mapPreviousGeoData}
      geoLocationData={mapGeoLocationData}
      isLoggedIn={isLoggedIn}
      isNotificationBannerVisible={isNotificationBannerVisible}
      fastResponderCriteria={fastResponderCriteria}
      isFastResponderFeatureEnabled={experiments.isFastResponderEnabled}
    />
  );
}
